import { Component, Input } from '@angular/core';
import { CardComponent } from '../../../core/panels/card.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';

// Interfaces for component inputs
export interface OffWorkData {
  status?: string;
  employmentStatus?: string;
  sameEmployer?: string;
  salaryContinuance?: string;
  dateOfTermination?: string;
  disabilityEndReason?: string;
}

export interface TempDisabilityData {
  tempTotal?: number;
  tempPartial?: number;
}

export interface PermanentDisabilityData {
  ppdScheduled?: number;
  ppdUnscheduled?: number;
  permanentTotal?: number;
  death?: number;
}

@Component({
  selector: 'app-time-tracking-header-wc',
  imports: [CardComponent, DetailPanelComponent, DetailItemComponent],
  template: `
    <div class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 tw-items-left tw-justify-left tw-pl-3 tw-pr-3">
      <app-card class="tw-w-full sm:tw-w-[40%]" label="Work status" theme="Light">
        <div class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 sm:tw-gap-8">
          <div class="tw-w-full sm:tw-w-1/4 sm:tw-min-w-[130px]">
            <div
              class="tw-bg-[var(--sed-purple-40-color)] tw-border tw-rounded-xl tw-pl-2 tw-pt-2 tw-pb-2 tw-mb-4 tw-font-bold"
            >
              <div class="tw-pb-1 tw-whitespace-nowrap">Off work</div>
              <span class="tw-text-[28px] tw-leading-none tw-pr-2">{{
                offWorkDays
              }}</span>
              <span class="tw-text-[13px]">days</span>
            </div>
            <div
              class="tw-bg-[var(--sed-purple-40-color)] tw-border tw-rounded-xl tw-pl-2 tw-pt-2 tw-pb-2 tw-mb-4 tw-font-bold"
            >
              <div class="tw-pb-1 tw-whitespace-nowrap">Restricted duty</div>
              <span class="tw-text-[28px] tw-leading-none tw-pr-2">{{
                restrictedDutyDays
              }}</span>
              <span class="tw-text-[13px]">days</span>
            </div>
          </div>
          <div class="tw-w-full sm:tw-w-3/4">
            <app-detail-panel
              [dataSource]="offWork"
              class="tw-w-full"
              [showEmpty]="false"
            >
              <app-detail-item
                label="Status"
                name="status"
                [showEmpty]="false"
              />
              <app-detail-item
                label="Employment status"
                name="employmentStatus"
                [showEmpty]="false"
              />
              <app-detail-item
                label="Same employer"
                name="sameEmployer"
                [showEmpty]="false"
              />
              <app-detail-item
                label="Salary continuance"
                name="salaryContinuance"
                [showEmpty]="false"
              />
              <app-detail-item
                label="Date of termination"
                name="dateOfTermination"
                format="d"
                [showEmpty]="false"
              />
              />
              <app-detail-item
                label="Disability end reason"
                name="disabilityEndReason"
                [showEmpty]="false"
              />
            </app-detail-panel>
          </div>
        </div>
      </app-card>
      <app-card class="tw-w-full sm:tw-w-[40%]" label="Temporary disability" theme="Light">
        <div class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 sm:tw-gap-8">
          <div class="tw-w-full sm:tw-w-1/4 sm:tw-min-w-[130px]">
            <div
              class="tw-bg-[var(--sed-purple-40-color)] tw-border tw-rounded-xl tw-pl-2 tw-pt-2 tw-pb-2 tw-mb-4 tw-font-bold"
            >
              <div class="tw-pb-1 tw-tracking-wide">TTD</div>
              <span class="tw-text-[28px] tw-leading-none tw-pr-2">{{
                ttd
              }}</span>
              <span class="tw-text-[13px]">days</span>
            </div>
            <div
              class="tw-bg-[var(--sed-purple-40-color)] tw-border tw-rounded-xl tw-pl-2 tw-pt-2 tw-pb-2 tw-mb-4 tw-font-bold"
            >
              <div class="tw-pb-1 tw-tracking-wide">TPD</div>
              <span class="tw-text-[28px] tw-leading-none tw-pr-2">{{
                tpd
              }}</span>
              <span class="tw-text-[13px]">days</span>
            </div>
          </div>

          <div class="tw-w-full sm:tw-w-3/4">
            <app-detail-panel [dataSource]="tempDisability" class="tw-w-full">
              <app-detail-item label="Compensation rates" />
              <app-detail-item
                label="Temporary total"
                name="tempTotal"
                format="c"
              />
              <app-detail-item
                label="Temporary partial"
                name="tempPartial"
                format="c"
              />
            </app-detail-panel>
          </div>
        </div>
      </app-card>
      <app-card class="tw-w-full sm:tw-w-[20%]" label="Permanent disability" theme="Light">
        <app-detail-panel [dataSource]="tempPermDisability" class="tw-w-full">
          <app-detail-item label="Compensation rates" />
          <app-detail-item
            label="PPD scheduled"
            name="ppdScheduled"
            format="c"
            labelCase="none"
          />
          <app-detail-item
            label="PPD unscheduled"
            name="ppdUnscheduled"
            format="c"
            labelCase="none"
          />
          <app-detail-item
            label="Permanent total"
            name="permanentTotal"
            format="c"
          />
          <app-detail-item label="Death" name="death" format="c" />
        </app-detail-panel>
      </app-card>
    </div>
  `,
  styles: ``,
})
export class TimeTrackingHeaderWcComponent {
  @Input()
  restrictions?: number;

  @Input()
  ttd?: number;

  @Input()
  tpd?: number;

  @Input()
  offWorkDays?: number;

  @Input()
  restrictedDutyDays?: number;

  @Input()
  offWork?: OffWorkData;

  @Input()
  tempDisability?: TempDisabilityData;

  @Input()
  tempPermDisability?: PermanentDisabilityData;
}
